import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLoading } from '../../../contexts/LoadingContext';
import { getClientType as getSessionClientType } from '../../../services/clientConfig';
import { getClientFileUrl } from '../../../tools/paths/clientPaths';
import { SIMFIX_UPLOADS_PROJECT, loadSimfixDataset } from '../APIs/simfixData';

const DataContext = createContext(null);

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};

// URL `client_type` overrides the session client (kept for FcdMap SIMFIX area links)
export const resolveSimfixClientType = (search = '') => {
  const fromUrl = new URLSearchParams(search).get('client_type');
  return fromUrl || getSessionClientType() || 'default';
};

const EMPTY_METRICS = { flusso: 0, codaMedia: 0, ritardoMedio: 0 };
export const DEFAULT_MAP_CENTER = [44.30713712309106, 9.349106110408071];

export const computeSistemaMetrics = (graficiDataJson, scenarioId) => {
  if (!scenarioId || !graficiDataJson?.scenario) return EMPTY_METRICS;
  const scenarioData = graficiDataJson.scenario.find((s) => s.replicaAvg === scenarioId);
  if (scenarioData?.sistema) {
    return {
      flusso: scenarioData.sistema.flowSys || 0,
      codaMedia: scenarioData.sistema.qmeanSys || 0,
      ritardoMedio: scenarioData.sistema.dtimeSys || 0,
    };
  }
  return EMPTY_METRICS;
};

export const computeDefaultMapCenter = (dataJson) => {
  const firstScenario = dataJson?.scenario?.[0];
  if (firstScenario?.center) {
    return [firstScenario.center[1], firstScenario.center[0]];
  }
  return DEFAULT_MAP_CENTER;
};

export const DataProvider = ({ children }) => {
  const location = useLocation();
  const { showLoader, hideLoader } = useLoading();
  const clientType = useMemo(() => resolveSimfixClientType(location.search), [location.search]);
  const [reloadKey, setReloadKey] = useState(0);
  const [dataJson, setDataJson] = useState({});
  const [losDataJson, setLosDataJson] = useState({});
  const [graficiDataJson, setGraficiDataJson] = useState({});
  const [scenarioDescriptions, setScenarioDescriptions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const folderPath = getClientFileUrl(clientType, '', SIMFIX_UPLOADS_PROJECT);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setDataLoaded(false);
    showLoader('Caricamento simulazione...');

    loadSimfixDataset(clientType)
      .then(({ graph, los, grafici, descriptions }) => {
        if (cancelled) return;
        setDataJson(graph || {});
        setLosDataJson(los || {});
        setGraficiDataJson(grafici || {});
        setScenarioDescriptions(descriptions || {});
        setDataLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[SimFix] Error fetching data:', err);
        setError(err?.message || 'Failed to load data');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
        hideLoader();
      });

    return () => {
      cancelled = true;
      hideLoader();
    };
  }, [clientType, reloadKey, showLoader, hideLoader]);

  const getScenarios = useCallback(() => dataJson?.scenario || [], [dataJson]);

  const getScenarioByReplicaAvg = useCallback(
    (replicaAvg) => dataJson?.scenario?.find((s) => s.replicaAvg === replicaAvg) || null,
    [dataJson]
  );

  const getLosDataByReplicaAvg = useCallback(
    (replicaAvg) => losDataJson?.scenario?.find((s) => s.replicaAvg === replicaAvg) || null,
    [losDataJson]
  );

  const getGraficiDataByReplicaAvg = useCallback(
    (replicaAvg) => graficiDataJson?.scenario?.find((s) => s.replicaAvg === replicaAvg) || null,
    [graficiDataJson]
  );

  const getSistemaMetrics = useCallback(
    (scenarioId) => computeSistemaMetrics(graficiDataJson, scenarioId),
    [graficiDataJson]
  );

  const getDefaultMapCenter = useCallback(() => computeDefaultMapCenter(dataJson), [dataJson]);

  const refreshData = useCallback(() => setReloadKey((k) => k + 1), []);

  const value = {
    clientType,
    folderPath,
    dataJson,
    losDataJson,
    graficiDataJson,
    scenarioDescriptions,
    isLoading,
    error,
    dataLoaded,
    getScenarios,
    getScenarioByReplicaAvg,
    getLosDataByReplicaAvg,
    getGraficiDataByReplicaAvg,
    getSistemaMetrics,
    getDefaultMapCenter,
    refreshData,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export default DataContext;
